<template>
  <div class="flex overflow-auto lg:overflow-visible relative z-10">
    <div class="max-w-[160px] border-r border-grey-850 flex flex-col px-4 gap-3 pt-3">
      <PdfToolButton
        v-if="!readonly"
        :iconName="preposIcon"
        label="Pré-positionner"
        :isActive="isPreposMode"
        @toggle="toggleMode('prepos')"
        data-sentry-id="sideMenuPreposBtn"
        :count="{
          value: signaturePositions.length,
          label: positionsTooltipLabel,
        }"
      />
      <PdfToolButton
        v-if="!readonly"
        :iconName="signIcon"
        label="Signer"
        :isActive="isSignMode"
        @toggle="toggleMode('sign')"
        data-sentry-id="sideMenuSignBtn"
        class="pb-3 border-b border-grey-900"
        :count="{
          value: pendingSignatures.length,
          label: signaturesTooltipLabel,
        }"
      />
      <PdfToolButton
        :iconName="commentIcon"
        label="Commenter le parapheur"
        :isActive="isCommentMode"
        @toggle="toggleMode('comment')"
        data-sentry-id="sideMenuCommentBtn"
      />
    </div>

    <PreposTool v-if="isPreposMode" :positions="signaturePositions" />

    <SignTool v-else-if="isSignMode" :id-doc-to-sign="document.id" :pendingSignatures="pendingSignatures" class="p-2" />

    <Comments v-else-if="isCommentMode" :readonly="readonly" class="w-full z-10" />
  </div>
</template>
!.id

<script setup lang="ts">
import type { LetterFileDocumentDto } from "@/app/dto/letter-file-document.dto.ts";
import { LetterFileStatus } from "@/app/dto/letter-file.dto.ts";
import { useLetterFileStore } from "@/app/stores/letter-file.store.ts";
import { usePendingSignaturesStore } from "@/app/stores/pending-signatures.store.ts";
import { useSignaturePositionStore } from "@/app/stores/signature-position.store.ts";
import { plural, replacePlural } from "@/app/utils/plural.ts";
import Comments from "@/app/views/LetterFileForm/Comments.vue";
import { canEditAfterArchival } from "@/app/views/LetterFileForm/utils.ts";
import type { PendingSignature } from "@/app/views/PdfViewer/domain/pending-signature.domain.ts";
import type { SignPosition } from "@/app/views/PdfViewer/domain/sign-position.domain.ts";
import PdfToolButton from "@/app/views/PdfViewer/tools/PdfToolButton.vue";
import { useIcons } from "@pasta/box-v2";
import { FaSignature, LaCommentDots, RiStickyNoteLine } from "oh-vue-icons/icons";
import { computed, ref } from "vue";
import PreposTool from "./PreposTool.vue";
import SignTool from "./SignTool.vue";

type Props = { document: LetterFileDocumentDto };
const props = defineProps<Props>();

const [preposIcon, signIcon, commentIcon] = useIcons(RiStickyNoteLine, FaSignature, LaCommentDots);

type ModeType = "prepos" | "sign" | "comment";
const mode = ref<ModeType | null>(null);

const letterFileStore = useLetterFileStore();
const readonly = computed(() => {
  const letterFile = letterFileStore.getCurrent()!;
  return letterFile.status !== LetterFileStatus.CURRENT || !canEditAfterArchival(letterFile, props.document);
});

const isPreposMode = computed(() => mode.value === "prepos");
const isSignMode = computed(() => mode.value === "sign");
const isCommentMode = computed(() => mode.value === "comment");

const signaturePositionStore = useSignaturePositionStore();
const signaturePositions = computed(() => {
  return signaturePositionStore.list as SignPosition[];
});
const positionsTooltipLabel = computed(() => {
  if (!signaturePositions.value.length) return "";
  return plural("pré-positionnement", signaturePositions.value.length, { prefixQty: true });
});

const pendingSignaturesStore = usePendingSignaturesStore();
const pendingSignatures = computed(() => {
  return pendingSignaturesStore.list as PendingSignature[];
});
const signaturesTooltipLabel = computed(() => {
  if (!pendingSignatures.value.length) return "";
  return replacePlural(pendingSignatures.value.length, "signature apposée", "signatures apposées", { prefixQty: true });
});

function toggleMode(val: ModeType) {
  if (mode.value === val) {
    mode.value = null;
  } else {
    mode.value = val;
  }
}
</script>
